import { SUPPORTED_LOCALE, useConfig } from '@travelwin/core';
import { createContext, ReactNode, useEffect, useRef, useState } from 'react';

import { useLocaleUtils } from '../../hooks/useLocaleUtils';
import { isClient } from '../../utils/ssrUtils';
import { useZendeskPositioningFix } from './useZendeskPositioningFix';

declare global {
  interface Window {
    zE?: any;
  }
}

/**
 * Locales available in Zendesk: https://support.zendesk.com/api/v2/locales/public.json
 */
const zendeskLocales: Record<SUPPORTED_LOCALE, string> = {
  de: 'de',
  el: 'el',
  en: 'en-ie',
  es: 'es',
  fr: 'fr',
  it: 'it',
  nl: 'nl',
  pl: 'pl',
  pt: 'pt',
  ro: 'ro',
  tr: 'tr',
  'pt-BR': 'pt-BR',
};

const toggleZendesk = (show: boolean) => {
  if (!window.zE) return;

  if (show) {
    window.zE('messenger', 'show');
  } else {
    window.zE('messenger', 'close');
    window.zE('messenger', 'hide');
  }
};

const setZendeskLocale = (locale: SUPPORTED_LOCALE) => {
  window?.zE('messenger:set', 'locale', zendeskLocales[locale]);
};

const createScript = (zendeskApiKey: string) => {
  const script = document.createElement('script');
  script.id = 'ze-snippet';
  script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskApiKey}`;
  return script;
};

const ZendeskMessenger = ({ show }: { show: boolean }) => {
  const { ZENDESK_API_KEY } = useConfig();
  const { currentLocale } = useLocaleUtils();
  const scriptRef = useRef<HTMLScriptElement | undefined>();
  const [zendeskReady, setZendeskReady] = useState(false);
  useZendeskPositioningFix();

  useEffect(() => {
    if (ZENDESK_API_KEY && !scriptRef.current) {
      const script = createScript(ZENDESK_API_KEY);
      scriptRef.current = script;
      script.addEventListener('load', () => {
        setZendeskLocale(currentLocale);
        setZendeskReady(true);
      });
      document.body.appendChild(script);
    }

    return () => {
      // We're just hiding on unmount because
      // The rendering part is handled by the zendesk script, and so we cannot be sure
      // what UI elements were added to the DOM.
      // This means once the script was loaded, we won't unload it.
      toggleZendesk(false);
    };
  }, [ZENDESK_API_KEY]);

  useEffect(() => {
    if (zendeskReady) {
      toggleZendesk(show);
    }
  }, [zendeskReady, show]);

  return null; // The Zendesk script itself is responsible for rendering
};

export const ZendeskContext = createContext<
  ((show: boolean) => void) | undefined
>(undefined);

export const ZendeskProvider = ({ children }: { children: ReactNode }) => {
  const { ZENDESK_API_KEY } = useConfig();
  const [mustShowZendesk, showZendesk] = useState(false);

  return (
    <ZendeskContext.Provider value={showZendesk}>
      {isClient() && ZENDESK_API_KEY?.trim() && (
        <ZendeskMessenger show={mustShowZendesk} />
      )}
      {children}
    </ZendeskContext.Provider>
  );
};
